import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface LocationContextType {
  selectedLocationId: string | undefined; // undefined = all locations
  setSelectedLocationId: (id: string | undefined) => void;
  isSuperAdmin: boolean;
  /** true when super_admin has selected a specific location (viewing as that location) */
  isLocationView: boolean;
  /** Called once when locations are loaded to set default if none saved */
  initializeDefault: (firstLocationId: string) => void;
}

const LocationContext = createContext<LocationContextType | null>(null);

const LOCATION_STORAGE_KEY = 'vitbikes_selected_location';
const LOCATION_ALL_SENTINEL = '__all__';

function readStoredLocation(): { hasPreference: boolean; locationId: string | undefined } {
  const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
  if (stored === null) return { hasPreference: false, locationId: undefined };
  if (stored === LOCATION_ALL_SENTINEL) return { hasPreference: true, locationId: undefined };
  return { hasPreference: true, locationId: stored };
}

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [selectedLocationId, setSelectedLocationIdState] = useState<string | undefined>(() => {
    const { locationId } = readStoredLocation();
    return locationId;
  });

  // Restore from localStorage when user/role changes
  useEffect(() => {
    const { locationId } = readStoredLocation();
    setSelectedLocationIdState(locationId);
  }, [isSuperAdmin]);

  const setSelectedLocationId = useCallback((id: string | undefined) => {
    setSelectedLocationIdState(id);
    if (id) {
      localStorage.setItem(LOCATION_STORAGE_KEY, id);
    } else {
      // Store sentinel so we know user explicitly chose "Alle Standorte"
      localStorage.setItem(LOCATION_STORAGE_KEY, LOCATION_ALL_SENTINEL);
    }
  }, []);

  const initializeDefault = useCallback((firstLocationId: string) => {
    const { hasPreference } = readStoredLocation();
    // Only auto-select if the user has never made a choice
    if (!hasPreference && isSuperAdmin) {
      setSelectedLocationIdState(firstLocationId);
      localStorage.setItem(LOCATION_STORAGE_KEY, firstLocationId);
    }
  }, [isSuperAdmin]);

  return (
    <LocationContext.Provider value={{ selectedLocationId, setSelectedLocationId, isSuperAdmin, isLocationView: isSuperAdmin && !!selectedLocationId, initializeDefault }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
}
